import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { applicationsAPI } from '../../services/api';
import BusinessLayout from '../../components/BusinessLayout';
import { 
  Star, 
  Search, 
  Eye, 
  CheckCircle, 
  Clock,
  Mail,
  Phone,
  Briefcase,
  MapPin,
  Calendar,
  XCircle
} from 'lucide-react';

interface Application {
  _id: string;
  job: {
    _id: string;
    title: string;
  };
  applicant: {
    user: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
    };
    title?: string;
    location?: string;
    skills?: string[];
  };
  status: string;
  createdAt: string;
}

const EmployerShortlisted: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await applicationsAPI.getMyJobApplications({ status: 'shortlisted' });
      setApplications(response.data.data || []);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredApps = applications.filter(app => {
    if (!search) return true;
    const fullName = `${app.applicant?.user?.firstName} ${app.applicant?.user?.lastName}`.toLowerCase();
    return fullName.includes(search.toLowerCase()) || 
           app.applicant?.user?.email?.toLowerCase().includes(search.toLowerCase());
  });

  const handleAction = async (appId: string, action: string) => {
    try {
      await applicationsAPI.updateStatus(appId, { status: action });
      fetchApplications();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  return (
    <BusinessLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Shortlisted Candidates</h1>
            <p className="text-gray-500 mt-1">Candidates you've shortlisted for interviews</p>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            <span className="text-sm text-gray-500">{filteredApps.length} candidates</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search shortlisted candidates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : filteredApps.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No shortlisted candidates yet</p>
            <Link to="/employer/applications" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
              Review applications
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredApps.map((app) => (
              <div key={app._id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                      <Star className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {app.applicant?.user?.firstName} {app.applicant?.user?.lastName}
                      </h3>
                      <p className="text-sm text-gray-500">{app.applicant?.title || 'Candidate'}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    <span>{app.job?.title}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <span className="truncate">{app.applicant?.user?.email}</span>
                  </div>
                  {app.applicant?.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{app.applicant?.location}</span>
                    </div>
                  )}
                </div>

                {app.applicant?.skills && app.applicant.skills.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {app.applicant.skills.slice(0, 3).map((skill, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                        {skill}
                      </span>
                    ))}
                    {app.applicant.skills.length > 3 && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                        +{app.applicant.skills.length - 3}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <Link
                    to={`/employer/applications?app=${app._id}`}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg text-sm hover:bg-blue-100 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </Link>
                  <button
                    onClick={() => handleAction(app._id, 'interviewed')}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-sm hover:bg-indigo-100 transition-colors"
                  >
                    <Clock className="w-4 h-4" />
                    Interview
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </BusinessLayout>
  );
};

export default EmployerShortlisted;
